import Promise from 'bluebird';

import { getURLWithScope } from './get-url-with-scope';

const MAX_POLL_ATTEMPTS = 60;

const pollIntervalTimeout = 5000;
const excelLimitExceededErrorCode = 18000;
const excelLimitExceededErrorString = 'TooMuchExcelDataToDownload';
const maxSuccessiveFailureCount = 5;

/**
 * Polls the status of a TaskItemExecution.
 * @param {number} options.taskItemExecutionId - Id of the TaskItemExecution.
 * @param {Function} options.getURLWithScope - Function to get the URL with the appropriate scope.
 * @param {Object} options.i18n - i18n library.
 * @param {Object} options.odata - odata library.
 * @param {Object} options.asyncScheduler - asyncScheduler library.
 * @param {Object} options.pageContext - PageContext library.
 * @param {number} options.maxPollCount - Max number of times to poll.
 * @returns {Promise} Resolves to the ResultId.
 */
export function pollTaskItemExecution({
  taskItemExecutionId,
  i18n,
  odata,
  asyncScheduler,
  pageContext,
  maxPollCount = MAX_POLL_ATTEMPTS,
  currentActivity = null,
}) {
  return new Promise((resolve, reject) => {
    let successiveFailureCount = 0;
    let pollCount = 0;

    // Define task and event handler
    const recurringTask = asyncScheduler.createTask({
      task: () => {
        pollCount += 1;
        if (pollCount > maxPollCount) {
          recurringTask.stop();
          reject(new Error(i18n.getString('GridDownloadFailureMessage')));
        }

        return odata.get(getURLWithScope(pageContext, `TaskItemExecutions(${taskItemExecutionId})`));
      },
      schedule: pollIntervalTimeout,
    });

    recurringTask.on('success', ({
      data,
      stopTask,
    }) => {
      // add log when get AzureBlobUrl from mt
      if (currentActivity) {
        currentActivity.trace({
          Id: data.Id,
          ResultId: data.ResultId,
          Status: data.Status,
        }, 'ReportDataTaskItemExecution/getAzureBlobUrl');
      }
      successiveFailureCount = 0;
      if (data) {
        const { Status, ResultId } = data;

        if (Status === 'Completed') {
          stopTask();
          resolve(ResultId);
        } else if (Status === 'Failed' || Status === 'RetryNeeded') {
          stopTask();

          let errorMessage = i18n.getString('GridDownloadFailureMessage');
          if (ResultId === excelLimitExceededErrorCode
            || ResultId === excelLimitExceededErrorString) {
            errorMessage = i18n.getString('GridDownloadLimit_RowsExceeded_Alert');
          }
          reject(new Error(errorMessage));
        }
      }
    });

    recurringTask.on('error', ({ error: eventError = new Error() }) => {
      successiveFailureCount += 1;
      if (maxSuccessiveFailureCount <= successiveFailureCount) {
        recurringTask.stop();
        reject(eventError);
      }
    });

    recurringTask.start();
  });
}
